/**
 * Training-feature API.
 *
 * Per the 2026-05-04 Phase-B graduation decision (Option A from the
 * gap-analysis discussion): lesson content is a static catalogue
 * bundled with the app (`@/data/training-lessons`); per-device progress
 * lives in `localStorage`. No network round-trips, no backend
 * dependency. The original Replit mock-api implementation backed these
 * functions with `fetch` calls against `/api/training/*`; that mock-api
 * is being discarded (RB-H1) and the Ktor backend has no training
 * endpoints yet, so this module ships entirely client-side.
 *
 * The four exported functions keep their original async signatures so
 * consuming pages (TrainingPage, LessonPage) don't need refactoring;
 * each just resolves immediately. When a Ktor training endpoint
 * exists, swap this module's implementations to call it — the public
 * shape is stable.
 */
import { getLessons as getLocalizedLessons } from "@/data/training-lessons";
import i18n from "@/i18n";
// ── Content-source resolution (Epic E) ───────────────────────────────────────
//
// Default = bundled (static catalogue), byte-identical to pre-Epic-E behavior.
// When the build is a preview build (VITE_PREVIEW_MODE) AND the URL carries a
// draft token or release_id, getLesson resolves through the preview source so
// authors see the in-progress draft (or a past prod release) in the SAME
// render components. Render path never changes; only the source does.
import {
  bundledContentSource,
  liveContentSource,
  previewContentSource,
  type ContentSource,
} from "@/lib/content-source";
import { API_BASE_URL } from "@/lib/api";
import { parsePreviewParams } from "@/lib/preview-params";

let testSourceOverride: ContentSource | null = null;

/** Test-only injection seam; pass null to reset. */
export function __setContentSourceForTest(source: ContentSource | null): void {
  testSourceOverride = source;
}

function resolveContentSource(): ContentSource {
  if (testSourceOverride) return testSourceOverride;

  // Author preview (drafts / past releases) takes precedence when enabled.
  const previewEnabled = import.meta.env.VITE_PREVIEW_MODE === "true";
  if (previewEnabled && typeof window !== "undefined") {
    const params = parsePreviewParams(window.location.search);
    if (params.mode !== "off") {
      const baseUrl = (import.meta.env.VITE_PREVIEW_API_BASE_URL ?? "") as string;
      if (baseUrl) {
        return previewContentSource({ baseUrl, token: params.token, releaseId: params.releaseId });
      }
    }
  }

  // Live backend (entitlement-gated /api/training/*) when a backend is configured
  // (VITE_API_BASE_URL); otherwise the bundled static catalogue. The live source
  // falls back to bundled on 401/error, so this is safe even before login.
  if (API_BASE_URL) return liveContentSource();
  return bundledContentSource;
}

// ── Types ────────────────────────────────────────────────────────────────────

export type LessonCategory =
  | "signs"
  | "speed"
  | "right-of-way"
  | "road-rules"
  | "alcohol-drugs"
  | "pedestrians"
  | "parking"
  | "weather";

export interface Lesson {
  id: string;
  title: string;
  category: LessonCategory;
  duration_min: number;
  emoji: string;
  colorFrom: string;
  colorTo: string;
  summary: string;
  tags: string[];
  completed: boolean;
}

export type LessonBlock =
  | { type: "heading"; text: string }
  | { type: "text"; body: string }
  | { type: "tip"; title?: string; body: string }
  | { type: "warning"; title?: string; body: string }
  | { type: "steps"; title?: string; items: string[] }
  | {
      type: "comparison";
      left_label: string;
      left: string;
      right_label: string;
      right: string;
    }
  | { type: "callout"; emoji: string; title: string; body: string }
  | { type: "image"; src: string; alt: string; caption?: string };

export interface CountryDiff {
  country_code: string;
  country_name: string;
  flag: string;
  summary: string;
  severity: "major" | "moderate" | "minor";
  details: string;
}

export interface LessonDetail extends Lesson {
  content: LessonBlock[];
  country_diffs: CountryDiff[];
  related_lesson_ids: string[];
  /**
   * Entitlement gate (ADR-0019), populated by the live backend source. When
   * `locked` is true the backend withheld `content` (empty) and the lesson page
   * shows the upgrade paywall. The bundled catalogue leaves these undefined
   * (everything visible offline).
   */
  locked?: boolean;
  requires_upgrade_to?: string | null;
}

/**
 * Lesson content as stored in the static catalogue — same shape as
 * `LessonDetail` minus the per-user `completed` flag, which is composed
 * in at runtime from `localStorage`.
 */
export type StaticLessonContent = Omit<LessonDetail, "completed">;

export interface LessonProgress {
  lesson_id: string;
  completed: boolean;
  completed_at_ms: number | null;
}

/**
 * A course — an ordered curriculum of lessons (served by /api/training/courses).
 * Field names mirror the backend `CourseDetailResponse` 1:1. `locked` (ADR-0019
 * gate) keeps the outline but routes the learner to the paywall.
 */
export interface Course {
  id: string;
  title: string;
  summary: string;
  state: string | null;
  license_class: string;
  focus: string;
  emoji: string | null;
  color: string | null;
  lesson_ids: string[];
  locked?: boolean;
  requires_upgrade_to?: string | null;
}

/**
 * Category metadata. `labelKey` resolves to the localized label via
 * `t(meta.labelKey)` at the render site; `label` keeps the English
 * fallback for non-React callers (e.g. seeding fixtures). React pages
 * must NOT use the `label` field — call `t(meta.labelKey)` instead so
 * the operator's UI language is honored.
 */
export const CATEGORY_META: Record<
  LessonCategory,
  { label: string; labelKey: string; emoji: string }
> = {
  signs: { label: "Signs & Signals", labelKey: "training.category.signs", emoji: "🚦" },
  speed: { label: "Speed & Zones", labelKey: "training.category.speed", emoji: "🏎️" },
  "right-of-way": { label: "Right of Way", labelKey: "training.category.rightOfWay", emoji: "🛑" },
  "road-rules": { label: "Road Rules", labelKey: "training.category.roadRules", emoji: "🛣️" },
  "alcohol-drugs": { label: "Alcohol & Drugs", labelKey: "training.category.alcoholDrugs", emoji: "🍺" },
  pedestrians: { label: "Pedestrians", labelKey: "training.category.pedestrians", emoji: "🚶" },
  parking: { label: "Parking", labelKey: "training.category.parking", emoji: "🅿️" },
  weather: { label: "Weather & Night", labelKey: "training.category.weather", emoji: "🌧️" },
};

// ── Progress (localStorage) ──────────────────────────────────────────────────
//
// Storage shape: `{ "<lesson-id>": { completed, completed_at_ms } }`.
// Versioned key so a future schema change can be a clean reset (we never
// store identifying data here, so reset on schema change is acceptable).

const STORAGE_KEY = "rb:training-progress:v1";

type ProgressEntry = { completed: boolean; completed_at_ms: number | null };
type ProgressMap = Record<string, ProgressEntry>;

function loadProgress(): ProgressMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ProgressMap) : {};
  } catch {
    return {};
  }
}

function saveProgress(progress: ProgressMap): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch {
    // Quota exceeded or storage unavailable — silently ignore. Progress
    // is convenience, not data we can't recompute by re-reading lessons.
  }
}

// ── Public API ───────────────────────────────────────────────────────────────

export async function getLessons(params?: {
  category?: LessonCategory;
  state?: string;
}): Promise<{ lessons: Lesson[] }> {
  const progress = loadProgress();
  // ROADS-529 Phase B: resolve lessons against the user's current i18n
  // locale instead of the hardcoded en bundle.
  // Epic E: source the list through the content-source seam; the preview
  // source returns an empty list for the single-element Live-Preview case, so
  // we fall back to the bundled catalogue there (unchanged default behavior).
  const sourced = await resolveContentSource().listLessons(i18n.language);
  const lessons =
    sourced.length > 0 ? sourced : getLocalizedLessons(i18n.language);
  const summaries: Lesson[] = lessons.map((l) => ({
    id: l.id,
    title: l.title,
    category: l.category,
    duration_min: l.duration_min,
    emoji: l.emoji,
    colorFrom: l.colorFrom,
    colorTo: l.colorTo,
    summary: l.summary,
    tags: [...l.tags],
    completed: progress[l.id]?.completed ?? false,
  }));
  const filtered = params?.category
    ? summaries.filter((l) => l.category === params.category)
    : summaries;
  return { lessons: filtered };
}

export async function getLesson(id: string): Promise<LessonDetail> {
  const content = await resolveContentSource().getLessonDetail(id, i18n.language);
  const progress = loadProgress();
  return {
    ...content,
    tags: [...content.tags],
    content: [...content.content],
    country_diffs: [...content.country_diffs],
    related_lesson_ids: [...content.related_lesson_ids],
    completed: progress[id]?.completed ?? false,
  };
}

export async function getCourses(): Promise<{ courses: Course[] }> {
  const src = resolveContentSource();
  const courses = src.listCourses ? await src.listCourses(i18n.language) : [];
  return { courses };
}

export async function getCourse(id: string): Promise<Course | null> {
  const src = resolveContentSource();
  return src.getCourse ? src.getCourse(id, i18n.language) : null;
}

export async function markLessonComplete(
  id: string,
  completed: boolean,
): Promise<void> {
  const progress = loadProgress();
  progress[id] = {
    completed,
    completed_at_ms: completed ? Date.now() : null,
  };
  saveProgress(progress);
}

export async function getTrainingProgress(): Promise<{
  progress: LessonProgress[];
  completedCount: number;
  totalCount: number;
}> {
  const progress = loadProgress();
  const lessons = getLocalizedLessons(i18n.language);
  const lessonProgress: LessonProgress[] = lessons.map((l) => ({
    lesson_id: l.id,
    completed: progress[l.id]?.completed ?? false,
    completed_at_ms: progress[l.id]?.completed_at_ms ?? null,
  }));
  return {
    progress: lessonProgress,
    completedCount: lessonProgress.filter((p) => p.completed).length,
    totalCount: lessons.length,
  };
}
